import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { Box, Container, Typography, InputBase } from "@mui/material";
import Pagination from "@mui/lab/Pagination";
import { Search, Building2, X, SlidersHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import sh from "bundles/common/utils/sh";
import trackEvent from "bundles/common/utils/trackEvent";
import { useIntl, FormattedMessage } from "react-intl";
import BackdropScreen from "bundles/common/components/BackdropScreen";
import CompanyCard from "./CompanyCard";
import FilterSidebar from "./FilterSidebar";
import useFollowCompany from "bundles/common/hooks/useFollowCompany";

const useQuery = () => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const CompanyIndex = () => {

  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const query = useQuery();
  const [companies, setCompanies] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [page, setPage] = useState(
    query.get("currentPage") ? parseInt(query.get("currentPage")) : 1
  );
  const [count, setCount] = useState(0);
  const [selectedIndustries, setSelectedIndustries] = useState(
    query.get("selectedIndustries") && query.get("selectedIndustries") != ""
      ? query.get("selectedIndustries")?.split(",")
      : []
  );
  // const [selectedSubSectors, setSelectedSubSectors] = useState([]);
  const [selectedBenefits, setSelectedBenefits] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queryText, setQueryText] = useState(
    history?.location?.state?.queryText || query.get("queryText") || ""
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const isInitialLoad = useRef(true);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const [debouncedQuery, setDebouncedQuery] = useState(queryText);
  const debounceTimerRef = useRef(null);
  const lastSearchSignatureRef = useRef(null);

  const { isFollowed, toggleFollow: handleFollowToggle } = useFollowCompany();

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(queryText);
    }, 700);
    return () => clearTimeout(debounceTimerRef.current);
  }, [queryText]);

  useEffect(() => {
    fetchCompanies({
      page,
      query: debouncedQuery,
      filters: createFilters(),
    });
  }, [page, debouncedQuery, selectedIndustries, selectedSizes, selectedLocations, selectedBenefits, i18nLocale]);

  const fetchCompanies = ({
    page = 1,
    query = queryText,
    filters = createFilters(),
    callback,
  } = {}) => {
    setLoading(true);

    sh.get("/search/companies", {
      params: {
        authenticity_token: authenticityToken,
        query,
        page,
        filters: JSON.stringify(filters),
        locale: i18nLocale,
      },
    })
      .then((res) => {
        const fetchedCompanies = res.data?.data.companies || [];
        setCompanies(fetchedCompanies);
        setPageCount(res.data?.pagy?.pages);
        setCount(res?.data?.pagy?.count);
        const signature = JSON.stringify({ query: query || "", filters });
        if (lastSearchSignatureRef.current !== null && lastSearchSignatureRef.current !== signature) {
          trackEvent("companies", "searched", {
            query: query || "",
            filters,
            result_count: res?.data?.pagy?.count || 0,
          });
        }
        lastSearchSignatureRef.current = signature;
        callback && callback(res.data?.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
        if (isInitialLoad.current) isInitialLoad.current = false;
      });
  };

  const createFilters = () => {
    let filters = {};
    if (selectedIndustries?.length > 0)
      Object.assign(filters, { company_industry: selectedIndustries });
    if (selectedSizes?.length > 0)
      Object.assign(filters, { team_size: selectedSizes });
    if (selectedLocations?.length > 0)
      Object.assign(filters, { city: selectedLocations });
    if (selectedBenefits?.length > 0)
      Object.assign(filters, { benefits: selectedBenefits });
    return filters;
  };

  const clearAdvancedFilters = () => {
    setSelectedIndustries([]);
    // setSelectedSubSectors([]);
    setSelectedBenefits([]);
    setSelectedSizes([]);
    setSelectedLocations([]);
  };

  const getCompanyUrl = (company) => {
    let baseUrl = `${window.location.origin}/${i18nLocale}/companies/${company.encoded_name}/${company.id}?redirectTo=companiesIndex`;
    if (queryText) {
      baseUrl += `&queryText=${queryText}`;
    }
    if (selectedIndustries.length > 0) {
      baseUrl += `&selectedIndustries=${selectedIndustries}`;
    }
    if (page > 1) {
      baseUrl += `&page=${page}`;
    }
    return baseUrl;
  };

  const totalActiveFilters =
    selectedIndustries.length +
    // selectedSubSectors.length +
    selectedBenefits.length +
    selectedSizes.length +
    selectedLocations.length;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
      {/* Hero */}
      <Box
        component="section"
        sx={{
          px: { xs: "16px", sm: "24px", lg: "32px" },
          pt: { xs: "40px", md: "56px" },
          pb: { xs: "24px", md: "32px" },
          background: "linear-gradient(to bottom, hsl(178, 30%, 97%), #fff)",
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ maxWidth: "1280px" }}>
          <Box component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "1.5rem", md: "2.25rem" },
                color: "hsl(178 93% 10%)",
                mb: 1,
                fontFamily: "Segoe UI Black",
              }}
            >
              <FormattedMessage id="company_index.title" />
            </Typography>
          </Box>

          <Box component={motion.div}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "1rem" },
                maxWidth: "42rem",
                color: "hsl(178 10% 45%)",
                fontFamily: "Segoe UI Regular",
              }}
            >
              <FormattedMessage id="company_index.subtitle" />
            </Typography>
          </Box>

          {/* Search bar */}
          <Box component={motion.div}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Box sx={{ mt: 3, maxWidth: "42rem" }}>
              <Box
                sx={{
                  position: "relative",
                  bgcolor: "#fff",
                  border: 1,
                  borderColor: "hsl(160, 10%, 90%)",
                  borderRadius: "12px",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  transition: "all 0.2s",
                  "&:focus-within": {
                    borderColor: "hsl(153, 68%, 25%)",
                    boxShadow: "0 0 0 3px hsla(153, 68%, 25%, 0.3)",
                  },
                }}
              >
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "hsl(178 10% 45%)",
                  }}
                />
                <InputBase
                  fullWidth
                  placeholder={intl.formatMessage({ id: "company_index.search_placeholder" })}
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "Segoe UI Regular",
                    "& .MuiInputBase-input": {
                      pl: "44px",
                      pr: 2,
                      py: "14px",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Active filter chips */}
      {selectedIndustries.length > 0 && (
        <Box sx={{ px: { xs: "16px", sm: "24px", lg: "32px" }, pb: 1.5 }}>
          <Container maxWidth={false} disableGutters sx={{ maxWidth: "1280px" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
              {selectedIndustries.map((industry) => (
                <Box
                  component="button"
                  key={industry}
                  onClick={() =>
                    setSelectedIndustries(
                      selectedIndustries.filter((i) => i !== industry)
                    )
                  }
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    fontFamily: "Segoe UI Regular",
                    bgcolor: "hsla(153, 68%, 25%, 0.1)",
                    color: "hsl(153, 68%, 25%)",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                    "&:hover": { bgcolor: "hsla(153, 68%, 25%, 0.2)" },
                  }}
                >
                  <FormattedMessage id={`company.company_industry.${industry}`} />
                  <X size={12} />
                </Box>
              ))}
              <Box
                component="button"
                onClick={clearAdvancedFilters}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  fontFamily: "Segoe UI Regular",
                  color: "hsl(178, 10%, 45%)",
                  ml: 0.5,
                  border: "none",
                  bgcolor: "transparent",
                  cursor: "pointer",
                  transition: "color 0.2s",
                  "&:hover": { color: "hsl(0, 84.2%, 60.2%)" },
                }}
              >
                <FormattedMessage id="company_index.clear_all" />
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Mobile filter toggle */}
      <Box sx={{ display: { xs: "block", lg: "none" }, px: { xs: "16px", sm: "24px" }, pb: 2 }}>
        <Box
          component="button"
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            fontSize: "0.875rem",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            color: "#022231",
            bgcolor: "#fff",
            border: 1,
            borderColor: "hsl(160, 10%, 90%)",
            borderRadius: "12px",
            cursor: "pointer",
            transition: "border-color 0.2s",
            "&:hover": { borderColor: "hsla(153, 68%, 25%, 0.4)" },
          }}
        >
          <SlidersHorizontal size={14} />
          <FormattedMessage id="company_index.filters" />
          {totalActiveFilters > 0 && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: "50%",
                bgcolor: "hsl(153, 68%, 25%)",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 700,
              }}
            >
              {totalActiveFilters}
            </Box>
          )}
        </Box>
      </Box>

      {/* Content: Sidebar + Grid */}
      <Box
        component="section"
        sx={{
          px: { xs: "16px", sm: "24px", lg: "32px" },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ maxWidth: "1280px" }}>
          <Box sx={{ display: "flex", gap: 4 }}>
            <Box sx={{ display: { xs: "none", lg: "block" }, width: 256, flexShrink: 0 }}>
              <Box
                sx={{
                  position: "sticky",
                  top: "88px",
                  height: "calc(100vh - 112px)",
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                  pr: 0.5,
                  // Slim, unobtrusive scrollbar for the filters pane
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "hsla(178, 10%, 45%, 0.3)",
                    borderRadius: "9999px",
                  },
                  scrollbarWidth: "thin",
                }}
              >
                <FilterSidebar
                  selectedIndustries={selectedIndustries}
                  setSelectedIndustries={setSelectedIndustries}
                  // selectedSubSectors={selectedSubSectors}
                  // setSelectedSubSectors={setSelectedSubSectors}
                  selectedBenefits={selectedBenefits}
                  setSelectedBenefits={setSelectedBenefits}
                  selectedSizes={selectedSizes}
                  setSelectedSizes={setSelectedSizes}
                  selectedLocations={selectedLocations}
                  setSelectedLocations={setSelectedLocations}
                  onClearAll={clearAdvancedFilters}
                  totalActiveFilters={totalActiveFilters}
                />
              </Box>
            </Box>

            {/* Sidebar – Mobile overlay */}
            <AnimatePresence>
              {mobileFiltersOpen && (
                <Box
                  component={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  sx={{
                    display: { lg: "none" },
                    position: "fixed",
                    inset: 0,
                    zIndex: 40,
                    bgcolor: "rgba(255,255,255,0.95)",
                    backdropFilter: "blur(4px)",
                    overflowY: "auto",
                    pt: "72px",
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Typography sx={{ fontSize: "1.125rem", fontFamily: "Segoe UI Bold" }}>
                        <FormattedMessage id="company_index.filters" />
                      </Typography>
                      <Box
                        component="button"
                        onClick={() => setMobileFiltersOpen(false)}
                        sx={{ p: 1, border: "none", bgcolor: "transparent", color: "#022231", cursor: "pointer" }}
                      >
                        <X size={20} />
                      </Box>
                    </Box>
                    <FilterSidebar
                      selectedIndustries={selectedIndustries}
                      setSelectedIndustries={setSelectedIndustries}
                      // selectedSubSectors={selectedSubSectors}
                      // setSelectedSubSectors={setSelectedSubSectors}
                      selectedBenefits={selectedBenefits}
                      setSelectedBenefits={setSelectedBenefits}
                      selectedSizes={selectedSizes}
                      setSelectedSizes={setSelectedSizes}
                      selectedLocations={selectedLocations}
                      setSelectedLocations={setSelectedLocations}
                      onClearAll={clearAdvancedFilters}
                      totalActiveFilters={totalActiveFilters}
                    />
                    <Box
                      component="button"
                      onClick={() => setMobileFiltersOpen(false)}
                      sx={{
                        width: "100%",
                        mt: 3,
                        py: 1.5,
                        bgcolor: "hsl(153, 68%, 25%)",
                        color: "#fff",
                        fontSize: "0.875rem",
                        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                        borderRadius: "12px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      {intl.formatMessage(
                        { id: "company_search.n_results" },
                        { result: count }
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </AnimatePresence>

            {/* Results */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Count badge */}
              <Box sx={{ mb: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    bgcolor: "hsl(153, 68%, 25%)",
                    color: "#fff",
                    borderRadius: "9999px",
                    fontSize: "0.875rem",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  }}
                >
                  <Building2 size={14} />
                  {intl.formatMessage(
                    { id: "company_search.n_results" },
                    { result: count }
                  )}
                </Box>
              </Box>

              {loading && <BackdropScreen open={loading} />}

              {companies.length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  {companies.map((company, i) => (
                    <CompanyCard
                      key={company.id}
                      company={company}
                      index={i}
                      enableAnimation={isInitialLoad.current}
                      onClick={() => {
                        window.location.href = getCompanyUrl(company);
                      }}
                      isFollowed={isFollowed(company.id)}
                      onFollowToggle={currentCandidate?.id ? handleFollowToggle : undefined}
                    />
                  ))}
                </Box>
              ) : (
                !loading && (
                  <Box sx={{ textAlign: "center", py: 10 }}>
                    <Building2
                      size={48}
                      style={{ margin: "0 auto", color: "hsla(178, 93%, 10%, 0.12)", marginBottom: 16 }}
                    />
                    <Typography sx={{ color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                      <FormattedMessage id="company_search.no_results" />
                    </Typography>
                    <Box
                      component="button"
                      onClick={() => {
                        setQueryText("");
                        clearAdvancedFilters();
                      }}
                      sx={{
                        mt: 1.5,
                        fontSize: "0.875rem",
                        color: "hsl(153, 68%, 25%)",
                        fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                        border: "none",
                        bgcolor: "transparent",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      <FormattedMessage id="company_index.reset_filters" />
                    </Box>
                  </Box>
                )
              )}

              {/* Pagination */}
              {pageCount > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Pagination
                    size="large"
                    onChange={(e, p) => setPage(p)}
                    count={pageCount}
                    page={page}
                    variant="outlined"
                    color="secondary"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CompanyIndex;
